import React, { useState, useEffect } from 'react';
import { Upload, Table, Button, Modal, Form, Input, InputNumber, Typography, Space, message, Progress, Tooltip, Tag, Checkbox, Switch, Spin } from 'antd';
import { UploadOutlined, CopyOutlined, DeleteOutlined, EditOutlined, EyeOutlined, LinkOutlined, ClockCircleOutlined, MailOutlined, UnlockOutlined } from '@ant-design/icons';
import api from '../services/api';
import { getShareUploadFields } from '../utils/shareSecurity';
import './Dashboard.css';

const { confirm } = Modal;

const { Title, Paragraph, Text } = Typography;

const Dashboard = () => {
  const [files, setFiles] = useState([]);
  const [fileGroups, setFileGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [passwordModal, setPasswordModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileGroup, setSelectedFileGroup] = useState(null);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [shareModal, setShareModal] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [uploadedFileId, setUploadedFileId] = useState(null);
  const [uploadedFileGroupId, setUploadedFileGroupId] = useState(null);
  const [copiedText, setCopiedText] = useState('');
  const [editPasswordModal, setEditPasswordModal] = useState(false);
  const [editingFileId, setEditingFileId] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const [deletingFileGroupId, setDeletingFileGroupId] = useState(null);
  const [extendModal, setExtendModal] = useState(false);
  const [extendingFileId, setExtendingFileId] = useState(null);
  const [extendingFileGroupId, setExtendingFileGroupId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [downloadsModal, setDownloadsModal] = useState(false);
  const [downloadsData, setDownloadsData] = useState([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [viewingFileId, setViewingFileId] = useState(null);
  const [viewingGroupId, setViewingGroupId] = useState(null);
  const [viewingRequireEmail, setViewingRequireEmail] = useState(false);
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [extendForm] = Form.useForm();
  const passwordlessShare = Form.useWatch('passwordless', form);

  useEffect(() => {
    fetchFiles();
    fetchFileGroups();
  }, []);

  const fetchFiles = async () => {
    try {
      const response = await api.get('/api/files/list');
      console.log('Files received from API:', response.data);
      console.log('Total files:', response.data.length);
      console.log('Deleted files:', response.data.filter(f => f.deletedAt).length);
      setFiles(response.data.filter(f => !f.fileGroupId)); // Only show files not in groups
    } catch (error) {
      message.error('Failed to fetch files');
    } finally {
      setLoading(false);
    }
  };

  const fetchFileGroups = async () => {
    try {
      const response = await api.get('/api/file-groups/list');
      console.log('File groups received from API:', response.data);
      setFileGroups(response.data);
    } catch (error) {
      console.error('Failed to fetch file groups:', error);
    }
  };

  const handleUpload = async () => {
    if (!fileList.length) {
      message.error('Please select files to upload');
      return;
    }

    if (fileList.length === 1) {
      // Single file upload
      const formData = new FormData();
      formData.append('file', fileList[0]);
      
      const shareFields = getShareUploadFields({
        passwordless: form.getFieldValue('passwordless'),
        customPassword: form.getFieldValue('customPassword'),
      });
      Object.entries(shareFields).forEach(([key, value]) => formData.append(key, value));
      
      const expirationDays = form.getFieldValue('expirationDays') || 14;
      formData.append('expirationDays', expirationDays);

      const requireEmail = form.getFieldValue('requireEmail');
      if (requireEmail) {
        formData.append('requireEmail', 'true');
      }

      setUploading(true);
      setUploadProgress(0);

      try {
        const response = await api.post('/api/files/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          },
        });

        if (response.data.password) {
          setGeneratedPassword(response.data.password);
          setUploadedFileId(response.data.file.id);
          setPasswordModal(true);
        } else if (response.data.passwordless) {
          handleShare(response.data.file);
          message.success('Passwordless share created');
        } else {
          message.success('File uploaded successfully');
        }

        form.resetFields();
        setFileList([]);
        fetchFiles();
      } catch (error) {
        message.error(error.response?.data?.error || 'Upload failed');
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    } else {
      // Multiple file upload
      handleMultipleUpload();
    }
  };

  const handleMultipleUpload = async () => {
    const formData = new FormData();
    fileList.forEach(file => {
      formData.append('files', file);
    });
    
    const shareFields = getShareUploadFields({
      passwordless: form.getFieldValue('passwordless'),
      customPassword: form.getFieldValue('customPassword'),
    });
    Object.entries(shareFields).forEach(([key, value]) => formData.append(key, value));
    
    const expirationDays = form.getFieldValue('expirationDays') || 14;
    formData.append('expirationDays', expirationDays);

    const requireEmail = form.getFieldValue('requireEmail');
    if (requireEmail) {
      formData.append('requireEmail', 'true');
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const response = await api.post('/api/file-groups/upload-multiple', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        },
      });

      if (response.data.password) {
        setGeneratedPassword(response.data.password);
        setUploadedFileGroupId(response.data.fileGroup.id);
        setSelectedFileGroup(response.data.fileGroup);
        setPasswordModal(true);
      } else if (response.data.passwordless) {
        handleShareGroup(response.data.fileGroup);
        message.success('Passwordless share created');
      } else {
        message.success('Files uploaded successfully');
      }

      form.resetFields();
      setFileList([]);
      fetchFileGroups();
    } catch (error) {
      message.error(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = (fileId) => {
    console.log('Delete clicked for file:', fileId);
    setDeletingFileId(fileId);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      if (deletingFileId) {
        console.log('Deleting file:', deletingFileId);
        await api.delete(`/api/files/${deletingFileId}`);
        message.success('File deleted successfully');
        setDeletingFileId(null);
        fetchFiles();
      } else if (deletingFileGroupId) {
        console.log('Deleting file group:', deletingFileGroupId);
        await api.delete(`/api/file-groups/${deletingFileGroupId}`);
        message.success('File group deleted successfully');
        setDeletingFileGroupId(null);
        fetchFileGroups();
      }
      setDeleteModal(false);
    } catch (error) {
      console.error('Delete error:', error);
      message.error(error.response?.data?.error || 'Failed to delete');
    }
  };

  const handleUpdatePassword = (fileId) => {
    setEditingFileId(fileId);
    setEditPasswordModal(true);
    passwordForm.resetFields();
  };

  const handlePasswordUpdate = async () => {
    try {
      const values = await passwordForm.validateFields();
      await api.put(`/api/files/${editingFileId}/password`, {
        password: values.newPassword,
      });
      setFiles(currentFiles => currentFiles.map(file => (
        file.id === editingFileId
          ? { ...file, passwordRequired: true, passwordless: false }
          : file
      )));
      setSelectedFile(currentFile => (
        currentFile?.id === editingFileId
          ? { ...currentFile, passwordRequired: true, passwordless: false }
          : currentFile
      ));
      await fetchFiles();
      message.success('Password updated successfully');
      setEditPasswordModal(false);
      passwordForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        return; // Validation error
      }
      message.error('Failed to update password. Please check and try again.');
    }
  };

  const handleShare = (file) => {
    const link = `${window.location.origin}/file/${file.id}`;
    setShareLink(link);
    setSelectedFile(file);
    setSelectedFileGroup(null);
    setShareModal(true);
  };

  const handleShareGroup = (fileGroup) => {
    const link = `${window.location.origin}/file-group/${fileGroup.id}`;
    setShareLink(link);
    setSelectedFileGroup(fileGroup);
    setSelectedFile(null);
    setShareModal(true);
  };

  const handleDeleteGroup = (groupId) => {
    console.log('Delete clicked for file group:', groupId);
    setDeletingFileGroupId(groupId);
    setDeleteModal(true);
  };

  const confirmDeleteGroup = async () => {
    try {
      console.log('Deleting file group:', deletingFileGroupId);
      await api.delete(`/api/file-groups/${deletingFileGroupId}`);
      message.success('File group deleted successfully');
      setDeleteModal(false);
      setDeletingFileGroupId(null);
      fetchFileGroups();
    } catch (error) {
      console.error('Delete error:', error);
      message.error(error.response?.data?.error || 'Failed to delete file group');
    }
  };

  const handleExtend = (fileId) => {
    setExtendingFileId(fileId);
    setExtendingFileGroupId(null);
    setExtendModal(true);
    extendForm.setFieldsValue({ additionalDays: 14 });
  };

  const handleExtendGroup = (groupId) => {
    setExtendingFileGroupId(groupId);
    setExtendingFileId(null);
    setExtendModal(true);
    extendForm.setFieldsValue({ additionalDays: 14 });
  };

  const handleExtendExpiration = async () => {
    try {
      const values = await extendForm.validateFields();
      
      if (extendingFileId) {
        await api.put(`/api/files/${extendingFileId}/extend`, {
          additionalDays: values.additionalDays,
        });
      } else if (extendingFileGroupId) {
        await api.put(`/api/file-groups/${extendingFileGroupId}/extend`, {
          additionalDays: values.additionalDays,
        });
      }
      
      message.success('Expiration extended successfully');
      setExtendModal(false);
      extendForm.resetFields();
      
      if (extendingFileId) {
        fetchFiles();
      } else {
        fetchFileGroups();
      }
    } catch (error) {
      if (error.errorFields) {
        return; // Validation error
      }
      message.error(error.response?.data?.error || 'Failed to extend expiration');
    }
  };

  const handleViewDownloads = async (fileId, requireEmail) => {
    setViewingFileId(fileId);
    setViewingGroupId(null);
    setViewingRequireEmail(requireEmail);
    setDownloadsModal(true);
    setDownloadsLoading(true);
    try {
      const response = await api.get(`/api/files/${fileId}/downloads`);
      setDownloadsData(response.data);
    } catch (error) {
      message.error('Failed to fetch download history');
    } finally {
      setDownloadsLoading(false);
    }
  };

  const handleViewGroupDownloads = async (groupId, requireEmail) => {
    setViewingGroupId(groupId);
    setViewingFileId(null);
    setViewingRequireEmail(requireEmail);
    setDownloadsModal(true);
    setDownloadsLoading(true);
    try {
      const response = await api.get(`/api/file-groups/${groupId}/downloads`);
      setDownloadsData(response.data);
    } catch (error) {
      message.error('Failed to fetch download history');
    } finally {
      setDownloadsLoading(false);
    }
  };

  const handleToggleRequireEmail = async (checked) => {
    try {
      if (viewingFileId) {
        await api.put(`/api/files/${viewingFileId}/require-email`, { requireEmail: checked });
        fetchFiles();
      } else if (viewingGroupId) {
        await api.put(`/api/file-groups/${viewingGroupId}/require-email`, { requireEmail: checked });
        fetchFileGroups();
      }
      setViewingRequireEmail(checked);
      message.success(`Email requirement ${checked ? 'enabled' : 'disabled'}`);
    } catch (error) {
      message.error('Failed to update email requirement');
    }
  };

  const copyToClipboard = async (text, type = 'Text') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedText(type);
      message.success({
        content: `${type} copied to clipboard!`,
        duration: 3,
        style: {
          marginTop: '20vh',
        },
      });
      setTimeout(() => setCopiedText(''), 3000);
    } catch (err) {
      message.error('Failed to copy');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const columns = [
    {
      title: 'File Name',
      dataIndex: 'originalName',
      key: 'originalName',
      ellipsis: true,
    },
    {
      title: 'Size',
      dataIndex: 'size',
      key: 'size',
      render: (size) => formatFileSize(size),
      width: 120,
    },
    {
      title: 'Uploaded',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleDateString(),
      width: 120,
    },
    {
      title: 'Downloads',
      dataIndex: 'accessCount',
      key: 'accessCount',
      render: (count, record) => (
        <Space size={4}>
          <Tag>{count || 0}</Tag>
          {record.requireEmail && (
            <Tooltip title="Email required">
              <MailOutlined style={{ color: '#1890ff', fontSize: 12 }} />
            </Tooltip>
          )}
          {record.passwordRequired === false && (
            <Tooltip title="Passwordless share">
              <UnlockOutlined style={{ color: '#52c41a', fontSize: 12 }} />
            </Tooltip>
          )}
        </Space>
      ),
      width: 110,
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date, record) => {
        // Check if file has been deleted
        if (record.deletedAt) {
          return (
            <Tooltip title="File has been deleted from storage. Re-upload to share again.">
              <Tag color="default" icon={<DeleteOutlined />}>Deleted</Tag>
            </Tooltip>
          );
        }
        
        const expiresAt = new Date(date);
        const now = new Date();
        const msLeft = expiresAt - now;
        const daysLeft = Math.round(msLeft / (1000 * 60 * 60 * 24));
        const hoursLeft = Math.floor((msLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        
        if (msLeft <= 0) {
          return <Tag color="red">Expired</Tag>;
        } else if (daysLeft === 0) {
          return <Tag color="red">{hoursLeft}h left</Tag>;
        } else if (daysLeft <= 3) {
          return <Tag color="orange">{daysLeft}d left</Tag>;
        } else {
          return <Tag color="green">{daysLeft}d left</Tag>;
        }
      },
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => {
        const isDeleted = !!record.deletedAt;
        
        if (isDeleted) {
          return (
            <Space size="small">
              <Tooltip title="File has been deleted. Re-upload to share again.">
                <Button
                  icon={<UploadOutlined />}
                  size="small"
                  disabled
                />
              </Tooltip>
              <Text type="secondary" style={{ fontSize: '12px' }}>Re-upload needed</Text>
            </Space>
          );
        }
        
        return (
          <Space size="small">
            <Tooltip title="View Downloads">
              <Button
                icon={<EyeOutlined />}
                size="small"
                onClick={() => handleViewDownloads(record.id, record.requireEmail)}
              />
            </Tooltip>
            <Tooltip title="Share">
              <Button
                icon={<LinkOutlined />}
                size="small"
                onClick={() => handleShare(record)}
              />
            </Tooltip>
            <Tooltip title="Extend Expiration">
              <Button
                icon={<ClockCircleOutlined />}
                size="small"
                onClick={() => handleExtend(record.id)}
              />
            </Tooltip>
            <Tooltip title="Update Password">
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => handleUpdatePassword(record.id)}
              />
            </Tooltip>
            <Tooltip title="Delete">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
                onClick={() => handleDelete(record.id)}
              />
            </Tooltip>
          </Space>
        );
      },
      width: 190,
    },
  ];

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <Title level={2}>File Management</Title>
      </div>

      <div className="upload-section">
        <Form form={form} layout="vertical">
          <Form.Item
            name="customPassword"
            label="Password protection"
            extra={passwordlessShare
              ? 'Disabled for this passwordless share'
              : 'Optional: leave empty to generate a secure password automatically'}
            rules={[
              { min: 12, message: 'Password must be at least 12 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                message: 'Password must contain uppercase, lowercase, number, and special character',
              },
            ]}
          >
            <Input.Password
              placeholder="Enter custom password"
              disabled={passwordlessShare}
            />
          </Form.Item>

          <div className={`passwordless-option${passwordlessShare ? ' passwordless-option-active' : ''}`}>
            <Form.Item
              name="passwordless"
              valuePropName="checked"
              initialValue={false}
              noStyle
            >
              <Checkbox
                onChange={(event) => {
                  if (event.target.checked) {
                    form.setFieldValue('customPassword', undefined);
                  }
                }}
              >
                <Text strong>Passwordless share</Text>
                <Tag className="passwordless-secondary-tag">Secondary option</Tag>
              </Checkbox>
            </Form.Item>
            <Text type="secondary" className="passwordless-help">
              No password will be requested. Anyone with the private 256-bit link can download,
              so share it only with intended recipients.
            </Text>
          </div>

          <Form.Item
            name="expirationDays"
            label="Expiration Days"
            initialValue={14}
            extra="File will be automatically deleted after this period"
            rules={[
              { type: 'number', min: 1, max: 365, message: 'Must be between 1 and 365 days' },
            ]}
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              placeholder="Days until expiration"
            />
          </Form.Item>

          <Form.Item name="requireEmail" valuePropName="checked">
            <Checkbox>Require email address on download page</Checkbox>
          </Form.Item>

          <Upload
            multiple
            beforeUpload={(file) => {
              setFileList(prev => [...prev, file]);
              return false; // Prevent automatic upload
            }}
            fileList={[]}
          >
            <Button
              icon={<UploadOutlined />}
              size="large"
            >
              Select Files
            </Button>
          </Upload>
          
          {fileList.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <Text>Selected {fileList.length} file(s)</Text>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={uploading}
                style={{ marginLeft: 16 }}
              >
                {uploading ? `Uploading ${uploadProgress}%` : 'Upload'}
              </Button>
              <Button
                onClick={() => setFileList([])}
                style={{ marginLeft: 8 }}
              >
                Clear
              </Button>
            </div>
          )}
          
          <Text type="secondary" style={{ marginTop: 8, display: 'block' }}>
            You can select multiple files to upload as a group with a single download link
          </Text>
        </Form>

        {uploading && (
          <Progress
            percent={uploadProgress}
            strokeColor={{
              '0%': '#B0569A',
              '100%': '#FAA71B',
            }}
            style={{ marginTop: 16 }}
          />
        )}
      </div>

      {fileGroups.length > 0 && (
        <>
          <Title level={3} style={{ marginTop: 32 }}>File Groups</Title>
          <Table
            columns={[
              {
                title: 'Files',
                key: 'files',
                render: (_, record) => (
                  <div>
                    <Text strong>{record.filesCount} files</Text>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {record.files.slice(0, 3).map(f => f.originalName).join(', ')}
                      {record.files.length > 3 && ` +${record.files.length - 3} more`}
                    </div>
                  </div>
                ),
              },
              {
                title: 'Total Size',
                dataIndex: 'totalSize',
                key: 'totalSize',
                render: (size) => formatFileSize(size),
                width: 120,
              },
              {
                title: 'Uploaded',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => new Date(date).toLocaleDateString(),
                width: 120,
              },
              {
                title: 'Downloads',
                dataIndex: 'accessCount',
                key: 'accessCount',
                render: (count, record) => (
                  <Space size={4}>
                    <Tag>{count || 0}</Tag>
                    {record.requireEmail && (
                      <Tooltip title="Email required">
                        <MailOutlined style={{ color: '#1890ff', fontSize: 12 }} />
                      </Tooltip>
                    )}
                    {record.passwordRequired === false && (
                      <Tooltip title="Passwordless share">
                        <UnlockOutlined style={{ color: '#52c41a', fontSize: 12 }} />
                      </Tooltip>
                    )}
                  </Space>
                ),
                width: 110,
              },
              {
                title: 'Expires',
                dataIndex: 'expiresAt',
                key: 'expiresAt',
                render: (date, record) => {
                  if (record.deletedAt) {
                    return (
                      <Tooltip title="File group has been deleted from storage. Re-upload to share again.">
                        <Tag color="default" icon={<DeleteOutlined />}>Deleted</Tag>
                      </Tooltip>
                    );
                  }
                  
                  const expiresAt = new Date(date);
                  const now = new Date();
                  const msLeft = expiresAt - now;
                  const daysLeft = Math.round(msLeft / (1000 * 60 * 60 * 24));
                  
                  if (msLeft <= 0) {
                    return <Tag color="red">Expired</Tag>;
                  } else if (daysLeft <= 3) {
                    return <Tag color="orange">{daysLeft}d left</Tag>;
                  } else {
                    return <Tag color="green">{daysLeft}d left</Tag>;
                  }
                },
                width: 100,
              },
              {
                title: 'Actions',
                key: 'actions',
                render: (_, record) => {
                  if (record.deletedAt) {
                    return (
                      <Space size="small">
                        <Tooltip title="File group has been deleted. Re-upload to share again.">
                          <Button
                            icon={<UploadOutlined />}
                            size="small"
                            disabled
                          />
                        </Tooltip>
                        <Text type="secondary" style={{ fontSize: '12px' }}>Re-upload needed</Text>
                      </Space>
                    );
                  }
                  
                  return (
                    <Space size="small">
                      <Tooltip title="View Downloads">
                        <Button
                          icon={<EyeOutlined />}
                          size="small"
                          onClick={() => handleViewGroupDownloads(record.id, record.requireEmail)}
                        />
                      </Tooltip>
                      <Tooltip title="Share">
                        <Button
                          icon={<LinkOutlined />}
                          size="small"
                          onClick={() => handleShareGroup(record)}
                        />
                      </Tooltip>
                      <Tooltip title="Extend Expiration">
                        <Button
                          icon={<ClockCircleOutlined />}
                          size="small"
                          onClick={() => handleExtendGroup(record.id)}
                        />
                      </Tooltip>
                      <Tooltip title="Delete">
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          size="small"
                          onClick={() => handleDeleteGroup(record.id)}
                        />
                      </Tooltip>
                    </Space>
                  );
                },
                width: 190,
              },
            ]}
            dataSource={fileGroups}
            rowKey="id"
            loading={loading}
            rowClassName={(record) => record.deletedAt ? 'deleted-row' : ''}
            pagination={{
              pageSize: 5,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} file groups`,
            }}
          />
        </>
      )}

      <Title level={3} style={{ marginTop: 32 }}>Individual Files</Title>
      <Table
        columns={columns}
        dataSource={files}
        rowKey="id"
        loading={loading}
        rowClassName={(record) => record.deletedAt ? 'deleted-row' : ''}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} files`,
        }}
      />

      <Modal
        title={uploadedFileGroupId ? "Files Uploaded Successfully" : "File Uploaded Successfully"}
        open={passwordModal}
        onCancel={() => {}}
        maskClosable={false}
        footer={[
          <Button key="ok" type="primary" onClick={() => {
            setPasswordModal(false);
            setUploadedFileId(null);
            setUploadedFileGroupId(null);
            setSelectedFileGroup(null);
          }}>
            OK
          </Button>,
        ]}
      >
        <Paragraph>
          Your {uploadedFileGroupId ? `${selectedFileGroup?.filesCount || ''} files have` : 'file has'} been uploaded successfully. 
          Here is the generated password:
        </Paragraph>
        <div className="password-display" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text code strong style={{ flex: 1 }}>{generatedPassword}</Text>
          <Button
            icon={<CopyOutlined />}
            size="small"
            type={copiedText === 'Password' ? 'primary' : 'default'}
            onClick={() => copyToClipboard(generatedPassword, 'Password')}
          >
            {copiedText === 'Password' ? 'Copied!' : 'Copy'}
          </Button>
        </div>
        <Paragraph style={{ marginTop: '16px' }}>Share link:</Paragraph>
        <div className="password-display" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Text code style={{ flex: 1, wordBreak: 'break-all' }}>
            {uploadedFileGroupId ? 
              `${window.location.origin}/file-group/${uploadedFileGroupId}` : 
              uploadedFileId ? `${window.location.origin}/file/${uploadedFileId}` : ''}
          </Text>
          <Button
            icon={<CopyOutlined />}
            size="small"
            type={copiedText === 'Share link' ? 'primary' : 'default'}
            onClick={() => copyToClipboard(
              uploadedFileGroupId ? 
                `${window.location.origin}/file-group/${uploadedFileGroupId}` : 
                `${window.location.origin}/file/${uploadedFileId}`, 
              'Share link'
            )}
          >
            {copiedText === 'Share link' ? 'Copied!' : 'Copy'}
          </Button>
        </div>
        <Button
          icon={<CopyOutlined />}
          type={copiedText === 'All' ? 'primary' : 'default'}
          onClick={() => copyToClipboard(
            `Document link: ${uploadedFileGroupId ?
              `${window.location.origin}/file-group/${uploadedFileGroupId}` :
              `${window.location.origin}/file/${uploadedFileId}`}\nPassword: ${generatedPassword}`,
            'All'
          )}
          style={{ marginTop: '16px', width: '100%' }}
        >
          {copiedText === 'All' ? 'Copied!' : 'Copy Link & Password'}
        </Button>
        {uploadedFileGroupId && selectedFileGroup && (
          <Paragraph type="secondary" style={{ marginTop: '16px' }}>
            Total size: {formatFileSize(selectedFileGroup.totalSize)}
          </Paragraph>
        )}
        <Paragraph type="warning" style={{ marginTop: '16px' }}>
          Please save this password securely. You will need it to access the {uploadedFileGroupId ? 'files' : 'file'}.
        </Paragraph>
        <Paragraph type="danger" style={{ marginTop: '8px', fontSize: '13px' }}>
          <strong>Important:</strong> This password cannot be retrieved after closing this window. Make sure to save it now!
        </Paragraph>
      </Modal>

      <Modal
        title={selectedFileGroup ? "Share File Group" : "Share File"}
        open={shareModal}
        onOk={() => setShareModal(false)}
        onCancel={() => setShareModal(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setShareModal(false)}>
            Done
          </Button>,
        ]}
      >
        <Paragraph>Share this link to allow others to download the {selectedFileGroup ? 'files' : 'file'}:</Paragraph>
        <Input
          value={shareLink}
          readOnly
          addonAfter={
            <Button
              icon={<CopyOutlined />}
              size="small"
              type={copiedText === 'Share link' ? 'primary' : 'default'}
              onClick={() => copyToClipboard(shareLink, 'Share link')}
            >
              {copiedText === 'Share link' ? 'Copied!' : 'Copy'}
            </Button>
          }
        />
        <Paragraph type="secondary" style={{ marginTop: 16 }}>
          {(selectedFileGroup || selectedFile)?.passwordRequired === false
            ? 'No password required—anyone with this private link can download.'
            : 'Recipients will need the file password to access it.'}
        </Paragraph>
        {selectedFile && selectedFile.passwordRequired !== false && (
          <>
            <Paragraph style={{ marginTop: 16 }}>File password:</Paragraph>
            <div className="password-display" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Text type="secondary" style={{ flex: 1 }}>
                Passwords are securely encrypted and cannot be retrieved. 
              </Text>
              <Button 
                size="small" 
                onClick={() => {
                  setShareModal(false);
                  handleUpdatePassword(selectedFile.id);
                }}
              >
                Update Password
              </Button>
            </div>
            <Text type="secondary" style={{ fontSize: '12px', marginTop: '8px' }}>
              Make sure to save the password when you create or update it.
            </Text>
          </>
        )}
      </Modal>

      <Modal
        title="Update File Password"
        open={editPasswordModal}
        onOk={handlePasswordUpdate}
        onCancel={() => {
          setEditPasswordModal(false);
          passwordForm.resetFields();
        }}
        okText="Update Password"
      >
        <Form form={passwordForm} layout="vertical">
          <Form.Item
            name="newPassword"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter a password' },
              { min: 12, message: 'Password must be at least 12 characters' },
              {
                pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
                message: 'Password must contain uppercase, lowercase, number, and special character',
              },
            ]}
          >
            <Input.Password 
              placeholder="Enter new password" 
              autoComplete="new-password"
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Delete File"
        open={deleteModal}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModal(false);
          setDeletingFileId(null);
        }}
        okText="Delete"
        okType="danger"
      >
        <p>Are you sure you want to delete this file? This action cannot be undone.</p>
      </Modal>

      <Modal
        title="Download History"
        open={downloadsModal}
        onCancel={() => {
          setDownloadsModal(false);
          setDownloadsData([]);
          setViewingFileId(null);
          setViewingGroupId(null);
        }}
        footer={[
          <Button key="ok" type="primary" onClick={() => {
            setDownloadsModal(false);
            setDownloadsData([]);
            setViewingFileId(null);
            setViewingGroupId(null);
          }}>
            Done
          </Button>,
        ]}
        width={700}
      >
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <MailOutlined />
          <Text strong>Require email for downloads:</Text>
          <Switch
            checked={viewingRequireEmail}
            onChange={handleToggleRequireEmail}
          />
        </div>
        {downloadsLoading ? (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin />
          </div>
        ) : downloadsData.length === 0 ? (
          <Paragraph type="secondary">No downloads yet.</Paragraph>
        ) : (
          <Table
            dataSource={downloadsData}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
            columns={[
              {
                title: 'Email',
                dataIndex: 'downloaderEmail',
                key: 'downloaderEmail',
                render: (email) => email || <Text type="secondary">—</Text>,
              },
              {
                title: 'IP Address',
                dataIndex: 'ipAddress',
                key: 'ipAddress',
                width: 140,
              },
              {
                title: 'Date/Time',
                dataIndex: 'accessedAt',
                key: 'accessedAt',
                render: (date) => new Date(date).toLocaleString(),
                width: 180,
              },
            ]}
          />
        )}
      </Modal>

      <Modal
        title={extendingFileId ? "Extend File Expiration" : "Extend File Group Expiration"}
        open={extendModal}
        onOk={handleExtendExpiration}
        onCancel={() => {
          setExtendModal(false);
          extendForm.resetFields();
          setExtendingFileId(null);
          setExtendingFileGroupId(null);
        }}
        okText="Extend Expiration"
      >
        <Form form={extendForm} layout="vertical">
          <Form.Item
            name="additionalDays"
            label="Additional Days"
            rules={[
              { required: true, message: 'Please enter number of days' },
              { type: 'number', min: 1, max: 365, message: 'Must be between 1 and 365 days' },
            ]}
          >
            <InputNumber
              min={1}
              max={365}
              style={{ width: '100%' }}
              placeholder="Days to extend"
            />
          </Form.Item>
          <p style={{ marginTop: 10, color: '#666' }}>
            This will extend the {extendingFileId ? "file's" : "file group's"} expiration date by the specified number of days.
          </p>
        </Form>
      </Modal>
    </div>
  );
};

export default Dashboard;
